import { createContext, useContext, useEffect, useState } from "react";
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    onAuthStateChanged,
    signOut,
    GoogleAuthProvider,
    signInWithPopup,
} from "firebase/auth";
import { doc, onSnapshot, setDoc, getDoc, collection, query, where, Timestamp } from "firebase/firestore";
import { auth, db } from "../firebase";

const userAuthContext = createContext();

export function UserAuthContextProvider({ children }) {
    const [user, setUser] = useState({});
    const [role, setRole] = useState(null);
    const [orgId, setOrgId] = useState(null);
    const [orgData, setOrgData] = useState(null);
    const [orgLoading, setOrgLoading] = useState(true);

    // Connection IDs exposed via context (portalId and slackTeamId from org doc)
    const [hubspotConnectionId, setHubspotConnectionId] = useState(null);
    const [slackConnectionId, setSlackConnectionId] = useState(null);

    function logIn(email, password) {
        return signInWithEmailAndPassword(auth, email, password);
    }
    function signUp(email, password) {
        return createUserWithEmailAndPassword(auth, email, password);
    }
    function logOut() {
        return signOut(auth);
    }
    function googleSignIn() {
        const googleAuthProvider = new GoogleAuthProvider();
        return signInWithPopup(auth, googleAuthProvider);
    }

    // Listen for auth state
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentuser) => {
            console.log("Auth", currentuser);
            setUser(currentuser);
            if (!currentuser) {
                setRole(null);
                setOrgId(null);
                setOrgData(null);
                setOrgLoading(true);
                setHubspotConnectionId(null);
                setSlackConnectionId(null);
            }
        });
        return () => unsubscribe();
    }, []);

    // Subscribe to own user doc to get role + orgId; auto-migrate if missing
    useEffect(() => {
        if (!user?.uid) return;

        const unsub = onSnapshot(doc(db, "users", user.uid), async (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                if (!data.role) {
                    // Auto-migrate existing user to admin with slim fields
                    await setDoc(doc(db, "users", user.uid), {
                        role: "admin",
                        orgId: user.uid,
                        email: user.email || "",
                    }, { merge: true });
                    // The onSnapshot will fire again with updated data
                } else {
                    setRole(data.role);
                    setOrgId(data.orgId);

                    // Auto-create org doc for admins if missing
                    if (data.role === "admin" && data.orgId === user.uid) {
                        const orgSnap = await getDoc(doc(db, "organizations", user.uid));
                        if (!orgSnap.exists()) {
                            await setDoc(doc(db, "organizations", user.uid), {
                                portalId: null,
                                slackTeamId: null,
                                agentIds: [],
                                members: [],
                                currencyCode: "USD",
                                createdAt: Timestamp.now(),
                            });
                        }
                    }
                }
            } else {
                // New user — doc doesn't exist yet (Signup handles creation)
                setRole(null);
                setOrgId(null);
            }
        });

        return () => unsub();
    }, [user]);

    // Subscribe to org doc, then to connection docs and agents
    useEffect(() => {
        if (!orgId) {
            setOrgData(null);
            setOrgLoading(true);
            setHubspotConnectionId(null);
            setSlackConnectionId(null);
            return;
        }

        setOrgLoading(true);

        // Assembled orgData pieces
        let orgDocData = {};
        let hubspotData = {};
        let slackData = {};
        let agentsData = {};
        let currentPortalId = null;
        let currentSlackTeamId = null;

        // Track sub-subscriptions so we can tear them down
        let hubspotUnsub = null;
        let slackUnsub = null;
        let agentsUnsub = null;

        const emitOrgData = () => {
            setOrgData({
                ...orgDocData,
                hubspotConnection: hubspotData,
                slackConnection: slackData,
                agents: agentsData,
            });
            setOrgLoading(false);
        };

        // Subscribe to the organizations doc to get portalId and slackTeamId
        const orgUnsub = onSnapshot(doc(db, "organizations", orgId), (snap) => {
            if (!snap.exists()) {
                setOrgData({});
                setOrgLoading(false);
                return;
            }
            const data = snap.data();
            orgDocData = data;
            const newPortalId = data.portalId || null;
            const newSlackTeamId = data.slackTeamId || null;

            setHubspotConnectionId(newPortalId);
            setSlackConnectionId(newSlackTeamId);

            // Re-subscribe to hubspotConnections if portalId changed
            if (newPortalId !== currentPortalId) {
                currentPortalId = newPortalId;
                if (hubspotUnsub) hubspotUnsub();
                if (newPortalId) {
                    hubspotUnsub = onSnapshot(doc(db, "hubspotConnections", newPortalId), (hSnap) => {
                        hubspotData = hSnap.exists() ? hSnap.data() : {};
                        emitOrgData();
                    });
                } else {
                    hubspotData = {};
                    hubspotUnsub = null;
                    emitOrgData();
                }
            }

            // Re-subscribe to slackConnections if slackTeamId changed
            if (newSlackTeamId !== currentSlackTeamId) {
                currentSlackTeamId = newSlackTeamId;
                if (slackUnsub) slackUnsub();
                if (newSlackTeamId) {
                    slackUnsub = onSnapshot(doc(db, "slackConnections", newSlackTeamId), (sSnap) => {
                        slackData = sSnap.exists() ? sSnap.data() : {};
                        emitOrgData();
                    });
                } else {
                    slackData = {};
                    slackUnsub = null;
                    emitOrgData();
                }
            }

            // If no hubspot and no slack, still emit so loading stops
            if (!newPortalId && !newSlackTeamId) {
                emitOrgData();
            }
        });

        // Subscribe to agents collection filtered by orgId
        const agentsQuery = query(collection(db, "agents"), where("orgId", "==", orgId));
        agentsUnsub = onSnapshot(agentsQuery, (snap) => {
            const map = {};
            snap.forEach((d) => { map[d.id] = d.data(); });
            agentsData = map;
            emitOrgData();
        });

        return () => {
            orgUnsub();
            if (hubspotUnsub) hubspotUnsub();
            if (slackUnsub) slackUnsub();
            if (agentsUnsub) agentsUnsub();
        };
    }, [orgId]);

    const isAdmin = role === "admin";

    return (
        <userAuthContext.Provider
            value={{
                user, logIn, signUp, logOut, googleSignIn,
                role, orgId, orgData, orgLoading, isAdmin,
                hubspotConnectionId, slackConnectionId,
            }}
        >
            {children}
        </userAuthContext.Provider>
    );
}

export function useUserAuth() {
    return useContext(userAuthContext);
}
